import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, FileText, Send, Loader2, Search, ShieldCheck, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  APPROVED: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  REJECTED: "bg-rose-100 text-rose-700",
  PAUSED: "bg-gray-200 text-gray-700",
};

function renderBody(text, params) {
  if (!text) return "";
  let out = text;
  (params || []).forEach((p, i) => {
    out = out.replaceAll(`{{${i + 1}}}`, p || `{{${i + 1}}}`);
  });
  return out;
}

export default function TemplateChooser({ conversationId, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/whatsapp/templates")
      .then(r => {
        if (r.data?.ok) { setTemplates(r.data.templates || []); setError(null); }
        else setError(r.data?.reason === "wa_or_waba_id_missing"
          ? "WHATSAPP_BUSINESS_ACCOUNT_ID env var missing — set it in backend/.env and restart."
          : `Meta API error: ${r.data?.error?.message || r.data?.reason}`);
      })
      .catch(() => setError("Failed to fetch templates"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => (t.name || "").toLowerCase().includes(q) || (t.body_text || "").toLowerCase().includes(q));
  }, [templates, search]);

  const pick = (t) => { setSelected(t); setParams(new Array(t.variable_count).fill("")); };

  const send = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/inbox/${conversationId}/send-template`, {
        template_name: selected.name,
        params,
        language: selected.language,
      });
      if (data?.ok) { toast.success("Template sent to customer's WhatsApp"); onSent?.(); onClose?.(); }
      else toast.error(data?.error?.message || data?.reason || "Failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="template-chooser-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#1a3c5e]" />
          <h2 className="font-bold text-[#1a3c5e] flex-1">Send Approved Template</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        {loading && <div className="p-10 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline" /> Loading templates...</div>}
        {!loading && error && (
          <div className="p-6 bg-rose-50 border-b border-rose-200">
            <div className="flex items-start gap-2 text-rose-800">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold">{error}</div>
                <div className="text-xs mt-1">Approved templates are created and managed in Meta Business Manager → WhatsApp Manager → Message Templates. Submit your template there, wait for Meta approval (~24h), then it will appear here.</div>
              </div>
            </div>
          </div>
        )}
        {!loading && !error && (
          <div className="flex-1 grid grid-cols-[300px_1fr] overflow-hidden">
            {/* Templates list */}
            <div className="border-r border-gray-200 flex flex-col overflow-hidden">
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..." className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="template-search" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 && <div className="p-4 text-center text-sm text-gray-400">No templates</div>}
                {filtered.map(t => (
                  <button
                    key={`${t.name}-${t.language}`}
                    onClick={() => pick(t)}
                    data-testid={`template-${t.name}`}
                    className={`w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-gray-50 ${selected?.name === t.name ? "bg-[#FEF3E2]" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-[#1a3c5e] truncate">{t.name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${STATUS_STYLE[t.status] || "bg-gray-100 text-gray-700"}`}>{t.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{t.body_text || t.category}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{t.language} · {t.variable_count} variable{t.variable_count !== 1 ? "s" : ""}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview / params */}
            <div className="flex flex-col overflow-hidden">
              {!selected && <div className="m-auto text-gray-400 text-sm">Pick a template to preview & send</div>}
              {selected && (
                <>
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <div className="text-xs text-gray-500 uppercase font-bold">Preview</div>
                    <div className="mt-2 p-3 bg-[#dcf8c6] rounded-2xl text-sm whitespace-pre-wrap shadow-sm" data-testid="template-preview">
                      {renderBody(selected.body_text, params)}
                    </div>
                    {selected.status !== "APPROVED" && (
                      <div className="mt-2 text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Status is {selected.status} — Meta may reject.</div>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {selected.variable_count === 0 ? (
                      <div className="text-sm text-gray-500">This template has no variables.</div>
                    ) : (
                      Array.from({ length: selected.variable_count }).map((_, i) => (
                        <div key={i}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Variable {`{{${i + 1}}}`}</label>
                          <input
                            value={params[i] || ""}
                            onChange={e => setParams(prev => { const c = [...prev]; c[i] = e.target.value; return c; })}
                            data-testid={`template-param-${i}`}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            placeholder={`Value for variable ${i + 1}`}
                          />
                        </div>
                      ))
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-100 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
                    <button onClick={send} disabled={sending || params.some(p => !p)} data-testid="send-template-btn" className="px-4 py-2 text-sm bg-[#25D366] hover:bg-[#1ea855] disabled:opacity-50 text-white font-bold rounded-lg inline-flex items-center gap-1">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Template
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
